import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { ReviewFormData } from '../../types';
import { reviewAPI } from '../../services/api';
import StarRating from './StarRating';
import './ReviewForm.css';

interface ReviewFormProps {
    productId: number;
    onReviewSubmitted?: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({ productId, onReviewSubmitted }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const isAuthenticated = !!localStorage.getItem('authTokens');

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file');
                return;
            }
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
            setImagePreview(null);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!isAuthenticated) {
            Swal.fire({
                title: t('reviews.loginRequired'),
                text: t('reviews.loginToReview'),
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: t('auth.login'),
                cancelButtonText: t('common.cancel'),
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/login');
                }
            });
            return;
        }

        if (rating === 0) {
            setError(t('reviews.ratingRequired'));
            return;
        }

        if (comment.trim().length < 10) {
            setError(t('reviews.commentTooShort'));
            return;
        }

        try {
            setSubmitting(true);
            const reviewData: ReviewFormData = {
                product: productId,
                rating,
                comment: comment.trim(),
                image: imageFile,
            };

            await reviewAPI.createReview(reviewData);

            Swal.fire({
                title: t('reviews.thankYou'),
                text: t('reviews.pendingApproval'),
                icon: 'success',
                confirmButtonText: t('common.ok'),
            });

            // Reset form
            setRating(0);
            setComment('');
            handleRemoveImage();

            if (onReviewSubmitted) {
                onReviewSubmitted();
            }
        } catch (err: any) {
            console.error('Error submitting review:', err);
            let errorMessage = '';
            if ((err.response?.status === 400 || err.response?.status === 500) && err.response?.data) {
                const errorData = err.response.data;
                const errStr = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
                if (errStr.includes('Duplicate entry') || errStr.includes('unique') || errStr.includes('already submitted')) {
                    errorMessage = i18n.language === 'bn' 
                        ? 'আপনি ইতিমধ্যে এই প্রোডাক্টটির জন্য একটি রিভিউ জমা দিয়েছেন।' 
                        : 'You have already submitted a review for this product.';
                } else if (errorData.non_field_errors) {
                    errorMessage = errorData.non_field_errors[0];
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else {
                    const firstKey = Object.keys(errorData)[0];
                    if (firstKey && Array.isArray(errorData[firstKey])) {
                        errorMessage = `${firstKey}: ${errorData[firstKey][0]}`;
                    } else {
                        errorMessage = JSON.stringify(errorData);
                    }
                }
            } else if (err.response?.status === 401) {
                errorMessage = i18n.language === 'bn' ? 'রিভিউ জমা দিতে দয়া করে লগইন করুন' : 'Please login to submit a review';
            } else {
                errorMessage = t('reviews.submitError');
            }
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="review-form-login-prompt">
                <p>{t('reviews.loginToReview')}</p>
                <button
                    className="btn btn-primary"
                    onClick={() => navigate('/login')}
                >
                    {t('auth.login')}
                </button>
            </div>
        );
    }

    return (
        <form className="review-form compact-review-form" onSubmit={handleSubmit}>
            {/* Top Inline Header Row */}
            <div className="review-form-header-row">
                <h3 className="review-form-title">
                    ✍️ {t('reviews.writeReview')}
                </h3>
                <div className="rating-selector-inline">
                    <span className="rating-selector-label">{t('reviews.yourRating')}:</span>
                    <StarRating
                        rating={rating}
                        onChange={setRating}
                        size="medium"
                    />
                </div>
            </div>

            {/* Compact Textarea with Inline Character Count */}
            <div className="compact-textarea-wrapper">
                <textarea
                    id="review-comment"
                    className="compact-form-textarea"
                    rows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('reviews.commentPlaceholder')}
                    disabled={submitting}
                />
                <span className="compact-char-counter">
                    {comment.length}/500
                </span>
            </div>

            {/* Error Message */}
            {error && (
                <div className="compact-form-error">
                    ⚠️ {error}
                </div>
            )}

            {/* Bottom Actions Row (Photo Attach + Submit Button) */}
            <div className="review-form-actions-row">
                <div className="photo-attach-wrapper">
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                        disabled={submitting}
                    />

                    {imagePreview ? (
                        <div className="mini-image-preview-card">
                            <img src={imagePreview} alt="Review preview" className="mini-image-preview-thumb" />
                            <button
                                type="button"
                                className="remove-mini-image-btn"
                                onClick={handleRemoveImage}
                                title="Remove Photo"
                                disabled={submitting}
                            >
                                ✕
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="compact-attach-photo-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={submitting}
                            title={i18n.language === 'bn' ? 'ছবি যুক্ত করুন (ঐচ্ছিক)' : 'Add Photo (Optional)'}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21 15 16 10 5 21" />
                            </svg>
                            <span>{i18n.language === 'bn' ? '📷 ছবি যুক্ত করুন' : '📷 Add Photo'}</span>
                        </button>
                    )}
                </div>

                <button
                    type="submit"
                    className="compact-submit-btn"
                    disabled={submitting || rating === 0 || comment.trim().length < 10}
                >
                    {submitting
                        ? (i18n.language === 'bn' ? 'জমা হচ্ছে...' : 'Submitting...')
                        : (i18n.language === 'bn' ? 'রিভিউ জমা দিন ✓' : 'Submit Review ✓')}
                </button>
            </div>
        </form>
    );
};

export default ReviewForm;
